import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { IS_DEVELOPMENT } from '~/modules/config';
import { areSameOrgIds, SALESFORCE_ORG_ID_PREFIX_LENGTH } from '~/modules/helpers/orgContextHelper';
import { AnalyticsEvent, AnalyticsService } from '~/modules/services/analyticsService';
import { SavedOrgsService } from '~/modules/services/savedOrgsService';
import { OrgInfo, UserService } from '~/modules/services/userService';
import { selectIsAuthenticated, userStore } from '~/modules/stores/userStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { logger } from '~/modules/utilities/logger';

const userService = new UserService();
const savedOrgsService = new SavedOrgsService();
const analyticsService = new AnalyticsService();
let tenantLoadPromise: Promise<void> | undefined;
let orgPickerSessionGeneration = 0;

// Fires an org-picker load-failure analytics event. Called from the store (no DOM node, so it can't
// dispatch a bubbling analytics event the way components do) — it drives AnalyticsService directly,
// the same store-fires-analytics pattern as contentStore.ts. The event carries only which loader
// failed; no org data. sendEvent already swallows/logs its own target errors, so this can't throw.
function reportLoadError(errorSource: 'loadTenants' | 'loadFavorites' | 'loadUserId') {
    // Store-originated event: there's no DOM node here to dispatch a bubbling analytics event the
    // way components do, so it drives AnalyticsService directly (same pattern as contentStore.ts).
    // Build on generateBaseAnalyticEvent() so the payload carries the same base enrichment
    // (viewport/device/url/token/timestamp) an <analytics-handler> event would, then layer on the
    // org-picker fields. GoogleTagManager.postEvent routes on topic+elementType and getBody reads
    // elementType+error_source. sendEvent swallows/logs its own target errors, so this can't throw.
    const event: AnalyticsEvent = {
        ...analyticsService.generateBaseAnalyticEvent(),
        topic: AnalyticsEventTopic.ANALYTICS_CLICK,
        eventType: 'click',
        elementType: ElementType.ORG_PICKER_ERROR,
        error_source: errorSource,
    };
    analyticsService.sendEvent(event);
}

export interface OrgPickerState extends State {
    orgs: OrgInfo[];
    // Service-provided total count (not derived from orgs.length); 0 until the first load resolves.
    totalCount: number;
    isLoading: boolean;
    // True once a load has resolved successfully; gates the once-per-session fetch so repeated
    // callers (header on auth + /select-org on connect, both re-mounting on full-page navs) dedupe.
    hasLoaded: boolean;
    // false once loaded (incl. a valid empty "0 orgs" result); true on any load failure. There is no
    // retry, so the failure is a plain flag — every error sends the user home the same way.
    error: boolean;
    // Org ids the user has pinned to the top. Loaded once per session from the savedOrgs API;
    // optimistically updated on toggle. On API failure the in-memory state is kept (degrades to
    // session-only per W-22901820 AC) rather than reverted. A new Set is created on every change
    // so WireStore's shallow equality check detects the update.
    favoriteOrgIds: ReadonlySet<string>;
    // Org ids that have been optimistically un-starred while loadFavorites() was in flight.
    // Subtracted from the server response on merge so an un-star during a GET is not silently reverted.
    pendingRemovals: ReadonlySet<string>;
    // True while the initial favorites fetch is in flight.
    isFavoritesLoading: boolean;
    // True once the favorites fetch has completed (success or failure); gates the once-per-session fetch.
    hasFavoritesLoaded: boolean;
    // userId for the authenticated user, loaded once per session via the user-context API.
    // On /select-org no org has been selected yet so userStore.userId is not set; this field
    // bridges that gap using the same sid-based auth that loadTenants() already uses.
    userId: string | undefined;
    // True while the userId fetch is in flight; prevents concurrent calls from double-fetching.
    isUserIdLoading: boolean;
    // True once userId has been fetched (success or failure); gates the once-per-session fetch.
    hasUserIdLoaded: boolean;
}

export function selectOrgPickerResolved(state: OrgPickerState): boolean {
    return state.hasLoaded || state.error;
}

const initialState: OrgPickerState = {
    orgs: [],
    totalCount: 0,
    isLoading: false,
    hasLoaded: false,
    error: false,
    favoriteOrgIds: new Set(),
    pendingRemovals: new Set(),
    isFavoritesLoading: false,
    hasFavoritesLoaded: false,
    userId: undefined,
    isUserIdLoading: false,
    hasUserIdLoaded: false,
};

export const orgPickerStore = createStore(
    initialState,

    (set: SetState<OrgPickerState>) => ({
        // Loads the tenant list once per store instance. Multiple callers (the header on auth, the
        // /select-org page on connect) await the same in-flight request or skip after it resolves,
        // so the header + page don't double-fetch or read an incomplete count.
        async loadTenants() {
            const { hasLoaded } = orgPickerStore.getState();
            if (hasLoaded) {
                return Promise.resolve();
            }
            if (tenantLoadPromise) {
                return tenantLoadPromise;
            }

            const generation = orgPickerSessionGeneration;
            const loadPromise = (async () => {
                set(function startLoading(draft: OrgPickerState) {
                    draft.isLoading = true;
                    draft.error = false;
                    return draft;
                });

                try {
                    const { orgs, totalCount } = await userService.getTenants();
                    if (generation !== orgPickerSessionGeneration) {
                        return;
                    }
                    set(function setOrgs(draft: OrgPickerState) {
                        draft.orgs = orgs;
                        draft.totalCount = totalCount;
                        draft.isLoading = false;
                        draft.hasLoaded = true;
                        return draft;
                    });
                } catch (error) {
                    if (generation !== orgPickerSessionGeneration) {
                        return;
                    }
                    handleException(error, 'orgPickerStore.loadTenants');
                    reportLoadError('loadTenants');
                    set(function setError(draft: OrgPickerState) {
                        draft.error = true;
                        draft.isLoading = false;
                        return draft;
                    });
                } finally {
                    if (generation === orgPickerSessionGeneration) {
                        tenantLoadPromise = undefined;
                    }
                }
            })();

            tenantLoadPromise = loadPromise;
            return loadPromise;
        },

        // Fetches the userId for the authenticated user once per session. Safe to call alongside
        // loadTenants() — they are independent. On /select-org the user has not yet selected an
        // org so userStore.userId is not set; this provides it via the same sid-based auth that
        // loadTenants() already uses. On any failure userId stays undefined and toggleFavorite
        // falls back to userStore (which may have been populated by the TBID flow in the meantime).
        async loadUserId() {
            const { isUserIdLoading, hasUserIdLoaded } = orgPickerStore.getState();
            if (isUserIdLoading || hasUserIdLoaded) {
                return;
            }

            const generation = orgPickerSessionGeneration;
            set(function startUserIdLoading(draft: OrgPickerState) {
                draft.isUserIdLoading = true;
                return draft;
            });

            try {
                const userInfo = await userService.getUser();
                if (generation !== orgPickerSessionGeneration) {
                    return;
                }
                // getUser() swallows its own request failure and resolves undefined rather than
                // rejecting (it's consumed elsewhere by an uncaught .then in userStore), so a load
                // failure surfaces here as a missing userInfo, not the catch below. Report it so the
                // failure is observable; userId stays undefined and toggleFavorite degrades to
                // userStore per W-22901820 AC.
                if (!userInfo) {
                    reportLoadError('loadUserId');
                }
                set(function setUserId(draft: OrgPickerState) {
                    draft.userId = userInfo?.userId;
                    draft.isUserIdLoading = false;
                    draft.hasUserIdLoaded = true;
                    return draft;
                });
            } catch (error) {
                if (generation !== orgPickerSessionGeneration) {
                    return;
                }
                handleException(error, 'orgPickerStore.loadUserId');
                reportLoadError('loadUserId');
                set(function clearUserIdLoading(draft: OrgPickerState) {
                    draft.isUserIdLoading = false;
                    draft.hasUserIdLoaded = true;
                    return draft;
                });
            }
        },

        // Fetches the user's pinned org ids from the savedOrgs API once per session. Safe to call
        // alongside loadTenants() — they are independent. savedOrgsService.getFavorites() rethrows
        // on failure (an empty list is a valid success, so a throw is the only failure signal); the
        // catch here reports the error and ends up with an empty set and hasFavoritesLoaded=true
        // (page works without pin-to-top, per W-22901820 AC).
        async loadFavorites() {
            const { isFavoritesLoading, hasFavoritesLoaded } = orgPickerStore.getState();
            if (isFavoritesLoading || hasFavoritesLoaded) {
                return;
            }

            const generation = orgPickerSessionGeneration;
            set(function startFavoritesLoading(draft: OrgPickerState) {
                draft.isFavoritesLoading = true;
                return draft;
            });

            try {
                const ids = await savedOrgsService.getFavorites();
                if (generation !== orgPickerSessionGeneration) {
                    return;
                }
                set(function setFavorites(draft: OrgPickerState) {
                    // Merge server ids with any optimistic adds applied while the GET was in flight,
                    // and subtract any optimistic removes so an un-star during a GET is not reverted.
                    const serverSet = new Set(ids);
                    const pendingAdds = [...draft.favoriteOrgIds].filter((id) => !serverSet.has(id));
                    const merged = new Set([...ids, ...pendingAdds]);
                    draft.pendingRemovals.forEach((id) => merged.delete(id));
                    draft.favoriteOrgIds = merged;
                    draft.pendingRemovals = new Set();
                    draft.isFavoritesLoading = false;
                    draft.hasFavoritesLoaded = true;
                    return draft;
                });
            } catch (error) {
                if (generation !== orgPickerSessionGeneration) {
                    return;
                }
                handleException(error, 'orgPickerStore.loadFavorites');
                reportLoadError('loadFavorites');
                set(function clearFavoritesLoading(draft: OrgPickerState) {
                    draft.isFavoritesLoading = false;
                    draft.hasFavoritesLoaded = true;
                    draft.pendingRemovals = new Set();
                    return draft;
                });
            }
        },

        // Optimistically toggles the pinned state for an org and syncs to the API. The in-memory
        // update is applied first so the UI responds immediately. On API failure the update is NOT
        // reverted — per W-22901820 AC, favorites degrade to session-only rather than showing an error.
        async toggleFavorite(orgId: string) {
            if (!orgId) {
                return;
            }
            // Snapshot the full required state in one call to keep orgs, userId, and favoriteOrgIds
            // consistent. Prefer the userId pre-loaded by loadUserId() (available on any auth path
            // including frontdoor.jsp). Fall back to userStore.userId which is set after org selection
            // via the TBID hydration flow.
            const { orgs, userId: storeUserId, favoriteOrgIds } = orgPickerStore.getState();
            // Reject orgIds outside the user's own tenant list — callers should only ever pass an id
            // from the current list, so anything else (stale UI state, a tampered event detail) is
            // rejected rather than silently favorited.
            if (!orgs.some((org) => areSameOrgIds(org.orgId, orgId))) {
                logger.warn(`orgPickerStore.toggleFavorite: orgId '${orgId}' is not in the current org list`);
                return;
            }
            const { userId: userStoreUserId, tpalInfo } = userStore.getState();
            let effectiveUserId = storeUserId ?? userStoreUserId;
            if (!effectiveUserId && tpalInfo?.length) {
                // Truncate both sides to 15 chars for comparison so the match works regardless of
                // whether the tenants API returns 15-char or 18-char IDs and TPAL holds the other.
                const orgId15 = orgId.slice(0, SALESFORCE_ORG_ID_PREFIX_LENGTH);
                const match = tpalInfo.find((t) => t.orgId.slice(0, SALESFORCE_ORG_ID_PREFIX_LENGTH) === orgId15);
                effectiveUserId = match?.userId;
            }
            const isCurrentlyFavorite = favoriteOrgIds.has(orgId);

            const next = new Set(favoriteOrgIds);
            if (isCurrentlyFavorite) {
                next.delete(orgId);
            } else {
                next.add(orgId);
            }

            // Apply the optimistic update unconditionally so the star responds immediately even
            // while loadUserId() is still in flight. The API sync is skipped if userId is unknown
            // (degrades to session-only per W-22901820 AC rather than suppressing the click).
            // Track pending removes so loadFavorites()'s merge can subtract them if the GET was in flight.
            set(function applyOptimisticToggle(draft: OrgPickerState) {
                draft.favoriteOrgIds = next;
                if (isCurrentlyFavorite) {
                    const nextRemovals = new Set(draft.pendingRemovals);
                    nextRemovals.add(orgId);
                    draft.pendingRemovals = nextRemovals;
                } else {
                    const nextRemovals = new Set(draft.pendingRemovals);
                    nextRemovals.delete(orgId);
                    draft.pendingRemovals = nextRemovals;
                }
                return draft;
            });

            if (!effectiveUserId) {
                return;
            }

            try {
                if (isCurrentlyFavorite) {
                    await savedOrgsService.removeFavorite(orgId, effectiveUserId);
                } else {
                    await savedOrgsService.addFavorite(orgId, effectiveUserId);
                }
            } catch {
                // API failure — keep the optimistic in-memory state (session-only per W-22901820 AC).
                // savedOrgsService calls handleException before rethrowing — no second log here.
            }
        },
        reset() {
            orgPickerSessionGeneration += 1;
            tenantLoadPromise = undefined;
            set(function reset() {
                return {
                    ...initialState,
                    favoriteOrgIds: new Set(),
                    pendingRemovals: new Set(),
                };
            });
        },
    }),
    'orgPickerStore',
    IS_DEVELOPMENT,
);

function resetOnLogout(isAuthenticated: boolean, wasAuthenticated: boolean): void {
    if (wasAuthenticated && !isAuthenticated) {
        orgPickerStore.reset();
    }
}

userStore.subscribe(resetOnLogout, selectIsAuthenticated);
